import type { PageCategoryType, PageContext } from '#types/gtm'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

import { camelCase } from 'scule'

// Given a page name such as "/about-us" It will return "About Us"
const getHumanizedPageName = (string: string) =>
  string.split('_')[0].replace(/-/g, ' ').replace(/\b\w/g, (char) =>
    char.toUpperCase())

export const getPageInfo = (
  { name, path, params }: RouteLocationNormalizedLoaded,
  pageData: PageContext
): [category?: PageCategoryType | undefined, ...payload: any[]] => {
  // Builds the pageNameEng from alternateLinks object
  const i18n = useI18n()
  const { $seo } = useNuxtApp()
  const enLocale = useRegion() === 'EMEA' ? 'en-gb' : 'en-us'
  const routeName = String(name || '')

  let pageNameEng: string | undefined

  // Builds the pageNameEng from alternateLinks object
  if (i18n.value.alternateLinks) {
    pageNameEng = i18n.value.alternateLinks?.[`/${routeName.split('_')[0]}`]?.[enLocale]
    pageNameEng && (pageNameEng = getHumanizedPageName(pageNameEng.split('/').pop() ?? ''))
  }

  if (pageData.pageCategory && pageData.pageName)
    return [pageData.pageCategory, pageData.pageName, pageNameEng || pageData.pageName]

  /*
   * Use $seo i18n to fetch pageName, pageNameEng, pageCategory
   * If these properties (or some of them) are not declared they will be filled according to the following
   * example: about-us
   * pageName: 'About Us' -> language dependent, last url path segment
   * pageNameEng: 'About Us' -> taken from "alternativeLinks" fetched within app.vue
   * pageCategory: 'Content' -> default value for custom content pages
   */
  const page = $seo[camelCase(routeName.split('_')[0])]

  if (page) {
    const pageName = getHumanizedPageName(path?.split('/')?.pop() || '')
    return [
      page.category || 'Content',
      page.name || pageName,
      page.nameEng || pageNameEng || page.name || pageName
    ]
  }

  // PLP, PDP, others
  const routeMap: Record<string, [PageCategoryType, ...any[]]> = {
    'c-slug': ['PLP', getPageBreadcrumb(pageData.breadcrumbs)?.join(' - ')],
    'p-slug': ['PDP', getUrlId(path, 'Product')],
    'search': ['Search', pageData.searchType],
    'agent-results': ['Ask Me Anything', 'Results', 'Results'],
    'slug': [params?.slug ? 'Content' : 'Home'],
  }
  if (routeMap[routeName]) return routeMap[routeName]
  if (path?.endsWith('/agent-results')) return routeMap['agent-results']
  return []
}

export default {
  onLoadPageData: (route: RouteLocationNormalizedLoaded, pageContext: PageContext = {}) => {
    const [placement, pageName, pageNameEng] = getPageInfo(route, pageContext)
    const { currency } = useRuntimeConfig().public
    const locale = useLocale()
    const { suggestedPhrase, searchType, searchTerm, searchResults, searchTermAdj, sourceId } = history.state
    const formatPageName = (category?: string, name?: string) => {
      if (!category && !name) return undefined
      return (category && name) && category !== name
        ? `${category}: ${name}`
        : category || name
    }
    const formattedPageNameEng = formatPageName(placement, pageContext.pageNameEng || pageNameEng)

    return {
      event: 'loadPageData',
      page: {
        addlBreadcrumbInfo: pageContext.breadcrumbs?.map(({ label, url }) => ({
          label,
          url,
          id: url?.split('-').pop()
        })),
        brand: useSiteName(),
        breadcrumb: getPageBreadcrumb(pageContext.breadcrumbs)?.join('|').toLowerCase(),
        categoryID: pageContext.categoryId,
        countryCode: useCountryCode(),
        countryLanguage: useLanguageCode().toLocaleLowerCase(),
        currencyCode: currency[locale],
        ...(placement && { pageCategory: placement }),
        ...((placement && pageName) && {
          pageName: formatPageName(placement, pageName)
        }),
        ...(formattedPageNameEng != null && { pageNameEng: formattedPageNameEng }),
        pageTitle: pageContext.pageTitle || document?.title,
        searchResults: pageContext.searchResults ?? searchResults,
        searchTerm: pageContext.searchTerm || searchTerm || suggestedPhrase,
        searchTermAdj: pageContext.searchTermAdj || searchTermAdj,
        searchType: searchType || (suggestedPhrase ? 'click' : pageContext.searchType),
        sourceId: pageContext.sourceId || sourceId,
      },
      _clear: true
    }
  },
  onRouterChange: ({ fullPath, name }: RouteLocationNormalizedLoaded) => ({
    'event': 'routerChange',
    'content-name': fullPath,
    'content-view-name': name
  })
}
